import React, { useEffect, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Button, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { matchSorter } from 'match-sorter';

import { hasSupervisorFeatureFlag } from '@paragon/login-tools/source/scripts/selectors/users';
import { sortBy } from '@paragon/utilities';

import { showCreateAssemblyGroupDialog } from '../../actions/dialogActions';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AssemblyGroupCard } from '../interface/cards/AssemblyGroupCard';

export function AssemblyGroupsPage() {
  const dispatch = useAppDispatch();
  const assemblyGroups = useAppSelector((state) => state.assemblyGroups.data);
  const isSupervisor = useAppSelector(hasSupervisorFeatureFlag);

  const [filter, setFilter] = useState('');

  const hasFilter = filter !== '';
  const clearFilter = () => setFilter('');

  const inputRef = useRef<HTMLInputElement>(undefined);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (hasFilter && event.code === 'Escape') {
        clearFilter();
        event.stopPropagation();
      } else if (event.ctrlKey && event.code === 'KeyF') {
        event.preventDefault();

        if (!inputRef.current) return;
        inputRef.current.focus();

        if (hasFilter) {
          inputRef.current.select();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hasFilter, setFilter, inputRef]);

  let assemblyGroupsArray = [...Object.values(assemblyGroups)];
  if (filter !== '') {
    assemblyGroupsArray = matchSorter(assemblyGroupsArray, filter, { keys: ['name'] });
  }
  const sortedFilteredAssemblyGroups = sortBy(assemblyGroupsArray, ({ created }) => created, 'desc');

  const cards = sortedFilteredAssemblyGroups.map((assemblyGroup) => (
    <AssemblyGroupCard key={assemblyGroup.guid} assemblyGroup={assemblyGroup} />
  ));

  return (
    <>
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 3 }}>
        <Typography variant="h4" sx={{ textAlign: 'center' }}>
          Find an Assembly Group
        </Typography>

        <TextField
          autoFocus
          fullWidth
          margin="normal"
          label="Assembly Group Name"
          value={filter}
          onChange={(event) => void setFilter(event.target.value)}
          onFocus={(event) => event.target.select()}
          inputRef={inputRef}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: hasFilter && (
                <InputAdornment position="end">
                  <IconButton onClick={() => clearFilter()}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Paper>

      {isSupervisor && (
        <Button
          fullWidth
          variant="outlined"
          sx={{ marginBottom: 3 }}
          onClick={() => void dispatch(showCreateAssemblyGroupDialog())}
        >
          Create new Assembly Group
        </Button>
      )}

      <Stack spacing={1}>{cards}</Stack>
    </>
  );
}
